// The ONE place in the web app that calls fetch(). Everything else — engine hooks, the
// legacy `lib/api.ts` façade, screens — goes through the client returned by createClient().
//
// Conventions (blueprint §5.1/§6): list endpoints take the query DSL
//   ?filter[field][op]=v&sort=-a,b&q=&expand=fk1&fields=&limit=&cursor=
// and return { data, next_cursor }. Errors are RFC 9457 Problem+JSON → ApiError.

import { requestStepUp } from './stepUp';

export type FilterOp = 'eq' | 'ne' | 'lt' | 'lte' | 'gt' | 'gte' | 'in' | 'nin' | 'like' | 'ilike' | 'is_null' | 'between' | 'contains';

export const FILTER_OPS: FilterOp[] = ['eq', 'ne', 'lt', 'lte', 'gt', 'gte', 'in', 'nin', 'like', 'ilike', 'is_null', 'between', 'contains'];

export interface FilterClause {
  field: string;
  op: FilterOp;
  value: unknown;
}

/**
 * A condition tree (`?where=`): leaves are clauses; a group matches ALL (and) or ANY (or) of its
 * children; groups nest. ANDed with `filter`.
 */
export type WhereNode = FilterClause | WhereGroup;
export type WhereGroup = { all: WhereNode[] } | { any: WhereNode[] };
export const isWhereGroup = (n: WhereNode): n is WhereGroup => 'all' in n || 'any' in n;

export interface ListQuery {
  /** Filter clauses; the same field may appear more than once with different ops. */
  filter?: FilterClause[];
  /** Nested AND/OR conditions. */
  where?: WhereGroup | null;
  /** Sort keys: 'name' ascending, '-created_at' descending. */
  sort?: string | string[];
  /** Free-text search over the resource's `searchFields`. */
  q?: string;
  /** Belongs-to expansions → `<field>_ref: {id, label}`. */
  expand?: string[];
  /** Sparse field set. */
  fields?: string[];
  limit?: number;
  cursor?: string | null;
  /** A view of the connected system of record (GET /<resource>/source-views), e.g. a Halo filter profile. */
  sourceView?: string | null;
}

export interface Paged<T> {
  data: T[];
  next_cursor?: string | null;
}

export interface RefValue {
  id: string;
  label: string;
}

function serializeValue(v: unknown): string {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map((x) => serializeValue(x)).join(',');
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (v instanceof Date) return v.toISOString();
  return String(v);
}

/** Builds the query string (with leading `?`, or '' when empty) exactly per the DSL. */
export function buildQueryString(q?: ListQuery): string {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const f of q.filter ?? []) {
    if (f.value === undefined) continue;
    p.append(`filter[${f.field}][${f.op}]`, serializeValue(f.value));
  }
  if (q.where) p.set('where', JSON.stringify(q.where));
  if (q.sort) {
    const s = Array.isArray(q.sort) ? q.sort.filter(Boolean).join(',') : q.sort;
    if (s) p.set('sort', s);
  }
  if (q.q && q.q.trim()) p.set('q', q.q.trim());
  if (q.expand?.length) p.set('expand', q.expand.join(','));
  if (q.fields?.length) p.set('fields', q.fields.join(','));
  if (q.limit) p.set('limit', String(q.limit));
  if (q.cursor) p.set('cursor', q.cursor);
  if (q.sourceView) p.set('source_view', q.sourceView);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Parses a DSL query string back into a ListQuery (URL ↔ state round-trips). */
export function parseQueryString(qs: string): ListQuery {
  const p = new URLSearchParams(qs.startsWith('?') ? qs.slice(1) : qs);
  const out: ListQuery = {};
  const filter: FilterClause[] = [];
  p.forEach((value, key) => {
    const m = key.match(/^filter\[([^\]]+)\]\[([^\]]+)\]$/);
    if (m) {
      const op = m[2] as FilterOp;
      let v: unknown = value;
      if (op === 'in' || op === 'nin' || op === 'between') v = value.split(',');
      if (op === 'is_null') v = value === 'true';
      filter.push({ field: m[1]!, op, value: v });
    }
  });
  if (filter.length) out.filter = filter;
  const where = p.get('where');
  if (where) {
    try {
      const w = JSON.parse(where) as WhereGroup;
      if (w && typeof w === 'object' && ('all' in w || 'any' in w)) out.where = w;
    } catch {
      /* a mangled link: ignore the conditions rather than break the list */
    }
  }
  const sort = p.get('sort');
  if (sort) out.sort = sort.split(',');
  const q = p.get('q');
  if (q) out.q = q;
  const expand = p.get('expand');
  if (expand) out.expand = expand.split(',');
  const fields = p.get('fields');
  if (fields) out.fields = fields.split(',');
  const limit = p.get('limit');
  if (limit) out.limit = Number(limit);
  const cursor = p.get('cursor');
  if (cursor) out.cursor = cursor;
  const sourceView = p.get('source_view');
  if (sourceView) out.sourceView = sourceView;
  return out;
}

// ── Errors ────────────────────────────────────────────────────────────────
export interface Problem {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /** zod `flatten()` shape or a plain field→messages map. */
  errors?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] } | Record<string, string[] | string>;
  [k: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly detail: string;
  readonly type: string | undefined;
  readonly problem: Problem | undefined;
  /** Field-level validation errors (field → messages), surfaced from the Problem body. */
  readonly fieldErrors: Record<string, string[]>;
  readonly formErrors: string[];

  constructor(status: number, message: string, problem?: Problem) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.title = problem?.title ?? message;
    this.detail = problem?.detail ?? message;
    this.type = problem?.type;
    const { fieldErrors, formErrors } = extractFieldErrors(problem);
    this.fieldErrors = fieldErrors;
    this.formErrors = formErrors;
  }

  get isValidation() {
    return this.status === 400 || this.status === 422;
  }
  get isConflict() {
    return this.status === 409;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isUnauthorized() {
    return this.status === 401;
  }
  get isNetwork() {
    return this.status === 0;
  }
}

function extractFieldErrors(problem?: Problem): { fieldErrors: Record<string, string[]>; formErrors: string[] } {
  const fieldErrors: Record<string, string[]> = {};
  const formErrors: string[] = [];
  const errs = problem?.errors;
  if (!errs || typeof errs !== 'object') return { fieldErrors, formErrors };
  const rec = errs as Record<string, unknown>;
  if ('fieldErrors' in rec && rec.fieldErrors && typeof rec.fieldErrors === 'object') {
    for (const [k, v] of Object.entries(rec.fieldErrors as Record<string, unknown>)) {
      if (Array.isArray(v)) fieldErrors[k] = v.map(String);
    }
    if (Array.isArray(rec.formErrors)) formErrors.push(...(rec.formErrors as unknown[]).map(String));
    return { fieldErrors, formErrors };
  }
  for (const [k, v] of Object.entries(rec)) {
    if (Array.isArray(v)) fieldErrors[k] = v.map(String);
    else if (typeof v === 'string') fieldErrors[k] = [v];
  }
  return { fieldErrors, formErrors };
}

// ── Client ────────────────────────────────────────────────────────────────
export type TokenProvider = () => Promise<string | null>;

export interface ClientOptions {
  /** Base URL; default VITE_API_URL or same-origin. */
  baseUrl?: string;
  /** Resource/meta path prefix; default '/v1'. The platform-admin engine uses '/v1/admin'. */
  prefix?: string;
  /** Called on a genuine 401 (not the "no tenant" onboarding 401). */
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

export interface RequestInitEx extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Raw body (FormData) — sent as-is without JSON encoding. */
  raw?: BodyInit;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** 'text': return the body as a string (an HTML document); 'blob': the raw bytes (a file). */
  as?: 'json' | 'text' | 'blob';
}

export interface ActionOpts {
  id?: string;
  body?: unknown;
  method?: 'POST' | 'GET' | 'PATCH' | 'PUT' | 'DELETE';
}

export type BulkOpName = 'update' | 'delete';

/** One row's outcome in a bulk operation. */
export interface BulkRowResult {
  id: string;
  ok: boolean;
  error?: { code: string; message: string };
}

/** POST /v1/<resource>/bulk response — per-id results plus tallies. */
export interface BulkResult {
  op: BulkOpName;
  results: BulkRowResult[];
  succeeded: number;
  failed: number;
}

/** Arguments to a bulk call, matching the backend body `{ op, ids, data? }`. */
export interface BulkOp {
  op: BulkOpName;
  ids: string[];
  data?: Record<string, unknown>;
}

export interface EntityRef {
  entity_type: string;
  entity_id: string;
}

export interface ResourceClient<Row = Record<string, unknown>, Create = Partial<Row>, Update = Partial<Row>> {
  readonly name: string;
  list(query?: ListQuery): Promise<Paged<Row>>;
  /** Walks every page (bounded by `max`) — for exports and small reference lists. */
  listAll(query?: ListQuery, max?: number): Promise<Row[]>;
  get(id: string, opts?: { expand?: string[]; fields?: string[] }): Promise<Row>;
  create(body: Create): Promise<Row>;
  update(id: string, body: Update): Promise<Row>;
  remove(id: string): Promise<void>;
  action<R = unknown>(name: string, opts?: ActionOpts): Promise<R>;
  /** Bulk update/delete over ids. `data` carries the fields to set on an 'update'. */
  bulk(op: BulkOpName, ids: string[], data?: Record<string, unknown>): Promise<BulkResult>;
  upload(id: string, file: File, extra?: Record<string, string>): Promise<Record<string, unknown>>;
}

export interface Client {
  request<T>(path: string, init?: RequestInitEx): Promise<T>;
  resource<Row = Record<string, unknown>, Create = Partial<Row>, Update = Partial<Row>>(name: string): ResourceClient<Row, Create, Update>;
  upload(entity: EntityRef, file: File, extra?: Record<string, string>): Promise<Record<string, unknown>>;
  readonly baseUrl: string;
  /** Resource/meta path prefix ('/v1' by default; '/v1/admin' for the admin engine). */
  readonly prefix: string;
}

export function createClient(getToken: TokenProvider, opts: ClientOptions = {}): Client {
  const baseUrl = opts.baseUrl ?? ((import.meta.env?.VITE_API_URL as string | undefined) ?? '');
  const prefix = opts.prefix ?? '/v1';
  const doFetch = opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));

  async function request<T>(path: string, init: RequestInitEx = {}): Promise<T> {
    const token = await getToken();
    const headers: Record<string, string> = { ...(init.headers as Record<string, string> | undefined) };
    if (token) headers['authorization'] = `Bearer ${token}`;
    let body: BodyInit | undefined;
    if (init.raw !== undefined) body = init.raw;
    else if (init.body !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(init.body);
    }
    let url = `${baseUrl}${path}`;
    if (init.query) {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(init.query)) if (v != null && v !== '') p.set(k, String(v));
      const s = p.toString();
      if (s) url += (url.includes('?') ? '&' : '?') + s;
    }
    const { body: _b, raw: _r, query: _q, as: _as, ...rest } = init;
    void _b;
    void _r;
    void _q;

    let res: Response;
    try {
      res = await doFetch(url, { ...rest, headers, body });
    } catch (e) {
      throw new ApiError(0, e instanceof Error ? `Network error: ${e.message}` : 'Network error');
    }
    if (!res.ok) {
      let problem: Problem | undefined;
      try {
        problem = (await res.json()) as Problem;
      } catch {
        /* no body */
      }
      if (res.status === 403 && problem?.title === 'step_up_required') {
        const reason = (problem as { errors?: { reason?: string } }).errors?.reason === 'stale' ? 'stale' : 'mfa_required';
        requestStepUp({ mode: 'verify', reason });
      }
      if (res.status === 401 && opts.onUnauthorized) {
        const detail = (problem?.detail ?? '').toLowerCase();
        // "no tenant" is the onboarding gate's signal, not an auth failure.
        if (!detail.includes('tenant')) opts.onUnauthorized();
      }
      throw new ApiError(res.status, problem?.detail ?? problem?.title ?? res.statusText ?? `HTTP ${res.status}`, problem);
    }
    if (res.status === 204) return undefined as T;
    if (init.as === 'blob') return (await res.blob()) as T;
    const text = await res.text();
    if (init.as === 'text') return text as T;
    if (!text) return undefined as T;
    return JSON.parse(text) as T;
  }

  function upload(entity: EntityRef, file: File, extra: Record<string, string> = {}) {
    const fd = new FormData();
    fd.set('entity_type', entity.entity_type);
    fd.set('entity_id', entity.entity_id);
    for (const [k, v] of Object.entries(extra)) fd.set(k, v);
    fd.set('file', file, file.name);
    return request<Record<string, unknown>>(`${prefix}/attachments`, { method: 'POST', raw: fd });
  }

  function resource<Row = Record<string, unknown>, Create = Partial<Row>, Update = Partial<Row>>(name: string): ResourceClient<Row, Create, Update> {
    const base = `${prefix}/${name}`;
    const enc = (id: string) => encodeURIComponent(id);
    return {
      name,
      list: (query) => request<Paged<Row>>(`${base}${buildQueryString(query)}`),
      listAll: async (query, max = 2000) => {
        const out: Row[] = [];
        let cursor: string | null | undefined = query?.cursor ?? null;
        do {
          const page: Paged<Row> = await request<Paged<Row>>(`${base}${buildQueryString({ ...query, cursor, limit: query?.limit ?? 200 })}`);
          out.push(...page.data);
          cursor = page.next_cursor ?? null;
        } while (cursor && out.length < max);
        return out;
      },
      get: (id, o) => request<Row>(`${base}/${enc(id)}${buildQueryString({ expand: o?.expand, fields: o?.fields })}`),
      create: (body) => request<Row>(base, { method: 'POST', body }),
      update: (id, body) => request<Row>(`${base}/${enc(id)}`, { method: 'PATCH', body }),
      remove: (id) => request<void>(`${base}/${enc(id)}`, { method: 'DELETE' }),
      action: (actionName, o = {}) =>
        request(`${base}${o.id != null ? `/${enc(o.id)}` : ''}/${actionName}`, {
          method: o.method ?? 'POST',
          ...(o.method === 'GET' ? {} : { body: o.body ?? {} }),
        }),
      bulk: (op, ids, data) => request<BulkResult>(`${base}/bulk`, { method: 'POST', body: { op, ids, ...(data ? { data } : {}) } }),
      upload: (id, file, extra) => upload({ entity_type: name, entity_id: id }, file, extra),
    };
  }

  return { request, resource, upload, baseUrl, prefix };
}
